// components/support/RaiseTicket.jsx
import React, { useState } from "react";
import { FaPaperclip, FaPaperPlane } from "react-icons/fa";
import { useLoader } from "../../../context/LoaderProvider.jsx";

const FoodRaiseTicket = () => {
  const [ticketData, setTicketData] = useState({
    ticketType: "",
    message: "",
    attachment: "",
    priority: "MEDIUM"
  });
  
  const [attachments, setAttachments] = useState([]);
  const { showLoader, hideLoader } = useLoader();

  const ticketTypes = [
    { value: "PAYOUT_ISSUE", label: "Payout Issue" },
    { value: "SUBSCRIPTION_ISSUE", label: "Subscription Issue" },
    { value: "ORDER_COMPLAINT", label: "Order Complaint" },
    { value: "TECHNICAL_SUPPORT", label: "Technical Support" },
    { value: "ACCOUNT_ISSUE", label: "Account Issue" },
    { value: "BILLING_ISSUE", label: "Billing Issue" },
    { value: "FEATURE_REQUEST", label: "Feature Request" },
    { value: "OTHER", label: "Other" }
  ];

  const priorities = [
    { value: "LOW", label: "Low", color: "text-success" },
    { value: "MEDIUM", label: "Medium", color: "text-warning" },
    { value: "HIGH", label: "High", color: "text-danger" },
    { value: "URGENT", label: "Urgent", color: "text-danger" }
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [];
    
    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 5MB.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        const base64Data = result.split(",")[1];
        newAttachments.push({
          name: file.name,
          type: file.type,
          size: file.size,
          base64: base64Data,
          preview: URL.createObjectURL(file)
        });
        
        if (newAttachments.length === files.length) {
          setAttachments(prev => [...prev, ...newAttachments]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTicketData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const vendorId = localStorage.getItem("vendorId");
    const token = localStorage.getItem("authToken");

    if (!vendorId || !token) {
      alert("Please login to submit a ticket.");
      return;
    }

    if (!ticketData.ticketType || !ticketData.message) {
      alert("Please fill all required fields.");
      return;
    }

    // Prepare attachment URLs (you can modify this based on your API)
    const attachmentUrls = attachments.map(att => ({
      name: att.name,
      type: att.type,
      data: att.base64
    }));

    const payload = {
      vendorId: Number(vendorId),
      ticketType: ticketData.ticketType,
      message: ticketData.message,
      priority: ticketData.priority,
      attachmentUrls: attachmentUrls,
      timestamp: new Date().toISOString()
    };

    try {
      showLoader();
      const response = await fetch(
        "https://backend.maamaas.com/food/api/vendor-tickets/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert("Ticket submitted successfully!");
        setTicketData({
          ticketType: "",
          message: "",
          attachment: "",
          priority: "MEDIUM"
        });
        setAttachments([]);
      } else {
        console.error("API Error:", result);
        alert(result.message || "Failed to submit ticket.");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      alert("Something went wrong while submitting the ticket.");
    } finally {
      hideLoader();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="raise-ticket-container">
      <div className="ticket-header">
        <h2>Raise a Support Ticket</h2>
        <p>Submit your issue and our team will get back to you soon.</p>
      </div>

      <form onSubmit={handleSubmit} className="ticket-form">
        {/* Table-like layout */}
        <div className="form-table">
          {/* Ticket Type Row */}
          <div className="form-row">
            <div className="form-label">
              <label htmlFor="ticketType">Ticket Type *</label>
            </div>
            <div className="form-input">
              <select
                id="ticketType"
                name="ticketType"
                value={ticketData.ticketType}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">Select a ticket type</option>
                {ticketTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Priority Row */}
          <div className="form-row">
            <div className="form-label">
              <label htmlFor="priority">Priority *</label>
            </div>
            <div className="form-input">
              <select
                id="priority"
                name="priority"
                value={ticketData.priority}
                onChange={handleInputChange}
                className="form-select"
              >
                {priorities.map((priority) => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
              <span className={`priority-badge ${priorities.find(p => p.value === ticketData.priority)?.color}`}>
                {priorities.find(p => p.value === ticketData.priority)?.label}
              </span>
            </div>
          </div>

          {/* Message Row */}
          <div className="form-row">
            <div className="form-label">
              <label htmlFor="message">Message *</label>
            </div>
            <div className="form-input">
              <textarea
                id="message"
                name="message"
                value={ticketData.message}
                onChange={handleInputChange}
                rows="5"
                placeholder="Describe your issue in detail..."
                required
                className="form-textarea"
              />
              <div className="char-count">
                {ticketData.message.length}/1000 characters
              </div>
            </div>
          </div>

          {/* Attachments Row */}
          <div className="form-row">
            <div className="form-label">
              <label>Attachments</label>
              <small className="text-muted">(Optional, max 5MB each)</small>
            </div>
            <div className="form-input">
              <div className="file-upload-area">
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  onChange={handleFileChange}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  className="file-input"
                />
                <label htmlFor="file-upload" className="file-upload-label">
                  <FaPaperclip className="me-2" />
                  Choose Files
                </label>
                <span className="file-hint">Click to select files or drag and drop</span>
              </div>

              {/* Attachments List */}
              {attachments.length > 0 && (
                <div className="attachments-list">
                  <h6>Selected Files:</h6>
                  {attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      <div className="attachment-info">
                        {file.type.startsWith('image/') ? (
                          <img 
                            src={file.preview} 
                            alt={file.name} 
                            className="attachment-preview"
                          />
                        ) : (
                          <div className="file-icon">
                            📄
                          </div>
                        )}
                        <div>
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{formatFileSize(file.size)}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="remove-btn"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="button" className="btn btn-outline-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary submit-btn">
            <FaPaperPlane className="me-2" />
            Submit Ticket
          </button>
        </div>

        {/* Help Text */}
        <div className="form-help">
          <div className="alert alert-info">
            <strong>Note:</strong>
            <ul className="mb-0 mt-2">
              <li>Please be specific about your issue for faster resolution.</li>
              <li>Our team typically responds within 24-48 hours.</li>
              <li>You can track your ticket status in the "My Tickets" section.</li>
              <li>For urgent matters, please call our support hotline.</li>
            </ul>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FoodRaiseTicket;